class StickyHeader extends HTMLElement {
  connectedCallback () {
    this.header = document.querySelector('.section-header')
    if (!this.header) return

    this.isStatic = this.getAttribute('data-sticky-type') === 'none'

    this.headerIsAlwaysSticky =
      this.getAttribute('data-sticky-type') === 'always' ||
      this.getAttribute('data-sticky-type') === 'reduce-logo-size'

    this.headerBounds = { top: 0, bottom: 0 }

    this.boundsReady = false

    this.setHeaderHeight = this.setHeaderHeight.bind(this)
    this.onScrollHandler = this.onScroll.bind(this)

    this.setHeaderHeight()

    this.headerBounds = this.header.getBoundingClientRect()

    this.mediaQuery = window.matchMedia('(max-width: 990px)')
    this.mediaQuery.addEventListener('change', this.setHeaderHeight)

    window.addEventListener('load', this.setHeaderHeight)
    window.addEventListener('pageshow', this.setHeaderHeight)

    if ('ResizeObserver' in window) {
      this.headerResizeObserver = new ResizeObserver(this.setHeaderHeight)
      this.headerResizeObserver.observe(this.header)
    }

    if (this.headerIsAlwaysSticky) {
      this.header.classList.add('shopify-section-header-sticky')
    }

    this.currentScrollTop = 0
    this.preventReveal = false

    this.predictiveSearch = this.querySelector('predictive-search')

    this.hideHeaderOnScrollUp = () => (this.preventReveal = true)

    this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp)
    window.addEventListener('scroll', this.onScrollHandler, { passive: true })

    this.createObserver()
    this.updateScrolledState()
  }

  disconnectedCallback () {
    this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp)
    window.removeEventListener('scroll', this.onScrollHandler)
    window.removeEventListener('load', this.setHeaderHeight)
    window.removeEventListener('pageshow', this.setHeaderHeight)
    this.mediaQuery?.removeEventListener('change', this.setHeaderHeight)
    this.headerResizeObserver?.disconnect()
  }

  setHeaderHeight () {
    document.documentElement.style.setProperty(
      '--header-height',
      `${this.header.offsetHeight}px`,
    )
  }

  createObserver () {
    const observer = new IntersectionObserver((entries, obs) => {
      if (!entries.length) return

      this.headerBounds = entries[0].boundingClientRect

      this.boundsReady = true

      obs.disconnect()
    })

    observer.observe(this.header)
  }

  updateScrolledState () {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const isStuck = scrollTop > 0 && this.header.getBoundingClientRect().top <= 0

    this.classList.toggle('hd-Header_Wrapper-scrolled', isStuck)
  }

  onScroll () {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop

    this.updateScrolledState()

    if (this.isStatic || !this.boundsReady) return

    if (this.predictiveSearch?.isOpen) return

    const bounds = this.headerBounds || { top: 0, bottom: 0 }

    if (scrollTop > this.currentScrollTop && scrollTop > bounds.bottom) {
      this.header.classList.add('scrolled-past-header')

      requestAnimationFrame(this.hide.bind(this))
    } else if (scrollTop < this.currentScrollTop && scrollTop > bounds.bottom) {
      this.header.classList.add('scrolled-past-header')

      if (!this.preventReveal) {
        requestAnimationFrame(this.reveal.bind(this))
      } else {
        clearTimeout(this.isScrolling)

        this.isScrolling = setTimeout(() => {
          this.preventReveal = false
        }, 66)

        requestAnimationFrame(this.hide.bind(this))
      }
    } else if (scrollTop <= bounds.top) {
      this.header.classList.remove('scrolled-past-header')
      requestAnimationFrame(this.reset.bind(this))
    }

    this.currentScrollTop = scrollTop
  }

  hide () {
    if (this.headerIsAlwaysSticky) return

    this.header.classList.add(
      'shopify-section-header-hidden',
      'shopify-section-header-sticky',
    )

    this.closeMenuDisclosure()
    this.closeSearchModal()
  }

  reveal () {
    if (this.headerIsAlwaysSticky) return

    this.header.classList.add('shopify-section-header-sticky', 'animate')
    this.header.classList.remove('shopify-section-header-hidden')
  }

  reset () {
    if (this.headerIsAlwaysSticky) return

    this.header.classList.remove(
      'shopify-section-header-hidden',
      'shopify-section-header-sticky',
      'animate',
    )
  }

  closeMenuDisclosure () {
    this.disclosures =
      this.disclosures || this.header.querySelectorAll('header-menu')

    this.disclosures.forEach(d => d?.close?.())
  }

  closeSearchModal () {
    this.searchModal =
      this.searchModal || this.header.querySelector('details-modal')

    this.searchModal?.close?.(false)
  }
}

customElements.define('sticky-header', StickyHeader)
